'use client';

import Box from '@mui/material/Box';
import { useCallback, useState } from 'react';
import { animated, type SpringValue } from 'react-spring';
import {
    GRUND,
    Gradientfält,
    ScenVäxlare,
    Siffervisare,
    TYPSNITT,
    hexTillRgba,
    ljus,
    type Kombonamn,
    type ScenKontext
} from 'src/components/rörelse';
import { PresentationsNav } from '../komponenter/PresentationsNav';
import { ScenSektion } from '../komponenter/ScenSektion';
import { hittaPresentation } from '../presentationer';
import { BRICKOR, DOKUMENT, SAMTALSLÄGEN, SPOTPRIS, UNDERHÅLLSPOSTER } from './mitt-hem-innehåll';

const AnimeradDiv = animated('div');

export function MittHemView() {
    const presentation = hittaPresentation('mitt-hem');
    const [glödIndex, sättGlödIndex] = useState(0);
    const glöd = useCallback(
        (index: number) => (synlig: boolean) => {
            if (synlig) sättGlödIndex(index);
        },
        []
    );

    return (
        <>
            <Gradientfält
                kombinationer={['grön', 'grön', 'grön', 'blå', 'lila', 'lila', 'lila']}
                positioner={[
                    [46, 40],
                    [26, 30],
                    [72, 40],
                    [34, 60],
                    [68, 52],
                    [30, 70],
                    [62, 34]
                ]}
                index={glödIndex}
                styrka={0.38}
            />

            <ScenSektion
                id="oversikt"
                kombo="grön"
                nivå="h1"
                rubrik={presentation.titel}
                brödtext={[
                    presentation.ingress,
                    'Bostaden är utgångspunkten. Det som rör den — förbrukning, ytor, handlingar, grannar — hänger ihop därifrån i stället för att ligga utspritt i olika system.'
                ]}
                onSynlig={glöd(0)}
            >
                {({ framsteg }: ScenKontext) => <Blomning framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="el"
                kombo="grön"
                spegla
                rubrik="Elpriset timme för timme"
                brödtext={[
                    'Spotpriset hämtas för det elområde du bor i och visas per timme för dygnet. Kvällstoppen syns direkt, och därmed också vilka timmar det är dyrt att tvätta.',
                    'Priserna är exklusive moms och kommer från elprisetjustnu.se.'
                ]}
                onSynlig={glöd(1)}
            >
                {({ framsteg }: ScenKontext) => <Spotprisdygn framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="varme"
                kombo="grön"
                rubrik="Och vad uppvärmningen faktiskt kostade"
                brödtext={[
                    'Föreningens fjärrvärmefakturor läses av och blir en förbrukningskurva över åren, med den verkliga kostnaden — inte en schablon.',
                    'Där mätare rapporterar in per lägenhet visas förbrukningen individuellt. Där de inte gör det säger vyn det, i stället för att visa en nolla som ser ut som ett mätvärde.'
                ]}
                onSynlig={glöd(2)}
            >
                <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    <Nyckeltal etikett="Fjärrvärme 2026" värde={1284000} suffix=" kr" kombo="grön" />
                    <Nyckeltal etikett="Förbrukning" värde={742} suffix=" MWh" kombo="grön" />
                </Box>
            </ScenSektion>

            <ScenSektion
                id="ytor"
                kombo="blå"
                spegla
                rubrik="Bostadens ytor och dess papper"
                brödtext={[
                    'Planlösningen ritas upp rum för rum och kan mätas. Byggnaden finns som modell. Bygglov och ritningar hämtas ur kommunens arkiv och energideklarationen från Boverket — handlingar som annars kräver att någon vet var de ska leta.'
                ]}
                onSynlig={glöd(3)}
            >
                {({ framsteg }: ScenKontext) => <Ytor framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="gatan"
                kombo="lila"
                vision
                visionstext="Nytt koncept"
                rubrik="Gatuchatten"
                brödtext={[
                    'En förening är en juridisk gräns, inte en social. Grannen tvärs över gatan hör till en annan förening men samma kvarter, samma sophämtning, samma snöröjning och samma inbrottsvåg.',
                    'Gatuchatten hänger på gatan i stället för på föreningen. Adressen är redan en stabil nyckel i registret, så samtalet följer huset och inte medlemskapet.'
                ]}
                onSynlig={glöd(4)}
            >
                {({ framsteg }: ScenKontext) => <Gatan framsteg={framsteg} />}
            </ScenSektion>

            <ScenSektion
                id="samtal"
                kombo="lila"
                spegla
                rubrik="Kanaler, enskilda samtal och grupper"
                brödtext={[
                    'Kanaler för det som angår alla, ämnen under dem för det som angår några. Enskilda samtal när det gäller en granne, och trådar med flera när det gäller ett trapphus.'
                ]}
                onSynlig={glöd(5)}
            >
                <Box sx={{ width: '100%', maxWidth: 440 }}>
                    <ScenVäxlare
                        etikett="Tre sätt att prata i huset"
                        kombo="lila"
                        höjd={230}
                        scener={SAMTALSLÄGEN.map((s) => ({
                            nyckel: s.nyckel,
                            etikett: s.etikett,
                            innehåll: <Samtalskort rubrik={s.rubrik} rader={s.rader} />
                        }))}
                    />
                </Box>
            </ScenSektion>

            <ScenSektion
                id="underhall"
                kombo="lila"
                vision
                rubrik="Underhållsplanen, som en tidslinje"
                brödtext={[
                    'En underhållsplan är inte tre årtal i ett formulär. Den är trettio år av åtgärder med kostnad, ordning och konsekvens för avgiften.',
                    'Utlagd på en tidslinje syns det som annars är svårt att se: när två stora poster krockar, och om avsättningen räcker när de gör det.'
                ]}
                onSynlig={glöd(6)}
            >
                {({ framsteg }: ScenKontext) => <Underhållstidslinje framsteg={framsteg} />}
            </ScenSektion>

            <PresentationsNav nuvarande="mitt-hem" />
        </>
    );
}

function Blomning({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box
            aria-label="Vad Mitt hem samlar"
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: 1.25,
                width: '100%',
                maxWidth: 420
            }}
        >
            {BRICKOR.map((bricka, i) => {
                const tröskel = i / BRICKOR.length;
                const kolumn = i % 3;
                const rad = Math.floor(i / 3);
                return (
                    <AnimeradDiv
                        key={bricka.id}
                        style={{
                            opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.18)),
                            transform: framsteg.to((p) => {
                                const t = p >= tröskel ? 1 : 0;
                                return `translate(${(1 - t) * (kolumn - 1) * -26}px, ${
                                    (1 - t) * (rad - 1) * -26
                                }px) scale(${0.86 + t * 0.14})`;
                            }),
                            willChange: 'transform, opacity'
                        }}
                    >
                        <Box
                            sx={{
                                px: 1.5,
                                py: 2,
                                borderRadius: 1.5,
                                textAlign: 'center',
                                fontSize: 13,
                                lineHeight: 1.25,
                                color: GRUND.text,
                                bgcolor: hexTillRgba(ljus(bricka.kombo), 0.1),
                                border: `1px solid ${hexTillRgba(ljus(bricka.kombo), 0.34)}`
                            }}
                        >
                            {bricka.etikett}
                        </Box>
                    </AnimeradDiv>
                );
            })}
        </Box>
    );
}

function Spotprisdygn({ framsteg }: { framsteg: SpringValue<number> }) {
    const max = Math.max(...SPOTPRIS);
    const topp = SPOTPRIS.indexOf(max);

    return (
        <Box sx={{ width: '100%', maxWidth: 520 }}>
            <Box
                aria-label={`Spotpris per timme, högst ${max} öre per kilowattimme klockan ${topp}`}
                sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: 190 }}
            >
                {SPOTPRIS.map((pris, timme) => {
                    const andel = pris / max;
                    const dyr = andel > 0.72;
                    const tröskel = timme / SPOTPRIS.length;
                    return (
                        <AnimeradDiv
                            key={timme}
                            style={{
                                flex: 1,
                                height: `${andel * 100}%`,
                                borderRadius: 2,
                                transformOrigin: 'bottom',
                                background: dyr ? ljus('lila') : ljus('grön'),
                                opacity: framsteg.to((p) => (p >= tröskel ? (dyr ? 1 : 0.75) : 0.16)),
                                transform: framsteg.to((p) => `scaleY(${p >= tröskel ? 1 : 0.06})`)
                            }}
                        />
                    );
                })}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1.25, fontSize: 11.5, color: GRUND.dämpad }}>
                <span>00</span>
                <span>06</span>
                <span>12</span>
                <span>18</span>
                <span>23</span>
            </Box>
            <Box sx={{ mt: 2, fontSize: 13.5, color: GRUND.dämpad }}>
                Dyrast klockan {topp}:00 — <Box component="span" sx={{ color: ljus('lila') }}>{max} öre/kWh</Box>.
                Billigast natten före, {Math.min(...SPOTPRIS)} öre.
            </Box>
        </Box>
    );
}

function Ytor({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 460, display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            <Hus framsteg={framsteg} />
            <Box>
                {DOKUMENT.map((d, i) => {
                    const tröskel = 0.35 + i / (DOKUMENT.length * 2);
                    return (
                        <AnimeradDiv
                            key={d.id}
                            style={{
                                opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.24)),
                                transform: framsteg.to((p) => `translateX(${p >= tröskel ? 0 : 16}px)`)
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    gap: 2,
                                    py: 1.3,
                                    borderBottom: `1px solid ${GRUND.kant}`,
                                    fontSize: 13.5
                                }}
                            >
                                <Box sx={{ color: GRUND.text }}>{d.namn}</Box>
                                <Box sx={{ color: GRUND.dämpad, textAlign: 'right' }}>{d.källa}</Box>
                            </Box>
                        </AnimeradDiv>
                    );
                })}
            </Box>
        </Box>
    );
}

/**
 * Trådmodell i SVG, med rotationen bunden till scrollen.
 *
 * Medvetet inte three.js, trots att @react-three/fiber redan finns i repot: ett statiskt
 * objekt på en textbaserad sida är inte värt megabyten det skulle kosta.
 */
function Hus({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <AnimeradDiv
            aria-hidden
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                transform: framsteg.to((p) => `perspective(760px) rotateY(${-26 + p * 52}deg)`),
                willChange: 'transform'
            }}
        >
            <Box
                component="svg"
                viewBox="0 0 240 170"
                sx={{ width: '100%', maxWidth: 260, height: 'auto', overflow: 'visible' }}
            >
                <g fill="none" stroke={ljus('blå')} strokeWidth="1.1" strokeLinejoin="round">
                    <path d="M40 70 L120 26 L200 70 L200 140 L120 160 L40 140 Z" opacity="0.9" />
                    <path d="M40 70 L120 90 L200 70" opacity="0.55" />
                    <path d="M120 90 L120 160" opacity="0.55" />
                    <path d="M40 104 L120 124 L200 104" opacity="0.3" />
                    <path d="M96 112 L96 146 M144 108 L144 142" opacity="0.3" />
                </g>
            </Box>
        </AnimeradDiv>
    );
}

function Gatan({ framsteg }: { framsteg: SpringValue<number> }) {
    const hus = ['12', '14', '16', '18', '20', '22'];
    return (
        <Box sx={{ width: '100%', maxWidth: 480 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 1 }}>
                {hus.map((nr, i) => {
                    const tröskel = i / hus.length;
                    return (
                        <AnimeradDiv
                            key={nr}
                            style={{
                                flex: 1,
                                opacity: framsteg.to((p) => (p >= tröskel ? 1 : 0.24)),
                                transform: framsteg.to((p) => `translateY(${p >= tröskel ? 0 : 14}px)`)
                            }}
                        >
                            <Box
                                sx={{
                                    height: 54 + (i % 3) * 16,
                                    borderRadius: '3px 3px 0 0',
                                    border: `1px dashed ${hexTillRgba(ljus('lila'), 0.55)}`,
                                    borderBottom: 'none',
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                    pb: 0.75,
                                    fontSize: 11.5,
                                    color: GRUND.dämpad
                                }}
                            >
                                {nr}
                            </Box>
                        </AnimeradDiv>
                    );
                })}
            </Box>
            <Box sx={{ height: 1, bgcolor: hexTillRgba(ljus('lila'), 0.5) }} />
            <Box sx={{ mt: 1.5, fontSize: 12.5, color: GRUND.dämpad, letterSpacing: '0.01em' }}>
                Klippgatan 12–22 · fyra föreningar, en gata
            </Box>
        </Box>
    );
}

function Samtalskort({ rubrik, rader }: { rubrik: string; rader: string[][] }) {
    return (
        <Box
            sx={{
                height: '100%',
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${hexTillRgba(ljus('lila'), 0.32)}`,
                bgcolor: hexTillRgba('#12151F', 0.8)
            }}
        >
            <Box
                sx={{
                    fontFamily: TYPSNITT.display,
                    fontVariationSettings: "'opsz' 36",
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    letterSpacing: '-0.015em',
                    mb: 2,
                    color: GRUND.text
                }}
            >
                {rubrik}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {rader.map(([vem, vad]) => (
                    <Box key={vem + vad} sx={{ fontSize: 13.5, lineHeight: 1.45 }}>
                        <Box component="span" sx={{ color: ljus('lila'), mr: 1 }}>
                            {vem}
                        </Box>
                        <Box component="span" sx={{ color: GRUND.dämpad }}>
                            {vad}
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

function Underhållstidslinje({ framsteg }: { framsteg: SpringValue<number> }) {
    return (
        <Box sx={{ width: '100%', maxWidth: 480 }}>
            {UNDERHÅLLSPOSTER.map((post, i) => {
                const tröskel = i / UNDERHÅLLSPOSTER.length;
                return (
                    <Box key={post.id} sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 0.75, fontSize: 13.5 }}>
                            <Box sx={{ color: GRUND.text }}>{post.åtgärd}</Box>
                            <Box data-tal sx={{ color: GRUND.dämpad, whiteSpace: 'nowrap' }}>
                                {post.år} · {post.kostnad}
                            </Box>
                        </Box>
                        <AnimeradDiv
                            style={{
                                height: 4,
                                borderRadius: 999,
                                background: `repeating-linear-gradient(90deg, ${ljus('lila')} 0 8px, transparent 8px 14px)`,
                                transformOrigin: 'left',
                                transform: framsteg.to((p) => `scaleX(${p >= tröskel ? 1 : 0})`)
                            }}
                        />
                    </Box>
                );
            })}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: GRUND.dämpad, mt: 2 }}>
                <span>2026</span>
                <span>2056</span>
            </Box>
        </Box>
    );
}

function Nyckeltal({
    etikett,
    värde,
    suffix,
    kombo
}: {
    etikett: string;
    värde: number;
    suffix?: string;
    kombo: Kombonamn;
}) {
    return (
        <Box>
            <Siffervisare
                till={värde}
                suffix={suffix}
                variant="h2"
                component="span"
                sx={{
                    display: 'block',
                    fontFamily: TYPSNITT.display,
                    fontVariationSettings: "'opsz' 96",
                    fontWeight: 600,
                    fontSize: 'clamp(1.9rem, 4.4vw, 3rem)',
                    lineHeight: 1,
                    letterSpacing: '-0.035em',
                    color: ljus(kombo)
                }}
            />
            <Box sx={{ fontSize: 13.5, color: GRUND.dämpad, mt: 0.75 }}>{etikett}</Box>
        </Box>
    );
}
